import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppNavbar from '../../components/AppNavbar';
import SiteFooter from '../../components/SiteFooter';
import MobileBottomNav from '../../components/MobileBottomNav';
import { authAPI } from '../../services/api';
import { User, Phone, Mail, Star, LogOut, Trash2 } from 'lucide-react';

// Google Play/App Store account-deletion requirement: self-service, no
// support ticket needed. Confirms with the PIN (mirrors backend/
// authController.js's deleteAccount, which requires it for any account
// that has one set) so a left-open session can't destroy the account alone.
const DeleteAccountModal = ({ onClose, onDeleted }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const confirmDelete = async () => {
    setError(''); setSubmitting(true);
    try {
      await authAPI.deleteAccount(pin ? { pin } : {});
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.message || 'تعذّر حذف الحساب، حاول مرة أخرى');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" dir="rtl">
        <h2 className="text-base font-bold text-red-700 mb-2">حذف الحساب</h2>
        <p className="text-sm text-gray-600 leading-relaxed mb-4">
          سيتم إلغاء تفعيل حسابك فوراً ولن تتمكن من تسجيل الدخول به مرة أخرى. هل أنت متأكد؟
        </p>
        <input
          type="password" inputMode="numeric" maxLength={6} placeholder="أدخل الرقم السري لتأكيد الحذف"
          value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-center tracking-[0.3em] font-bold focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
          dir="ltr"
        />
        {error && <p className="text-xs text-red-600 font-semibold mt-2">{error}</p>}
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} disabled={submitting}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm disabled:opacity-50">
            إلغاء
          </button>
          <button onClick={confirmDelete} disabled={submitting}
            className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold text-sm">
            {submitting ? '...جارٍ الحذف' : 'حذف نهائياً'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   PassengerProfilePage — حسابي. Notifications live in the standalone bell
   in AppNavbar now (NotificationBell), not here — this page is account
   info only.
───────────────────────────────────────────────────────────────────────────── */
const PassengerProfilePage = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('passenger_user') || '{}');
  const isLoggedIn = !!user?.id;
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) navigate('/login?from=/profile');
  }, [isLoggedIn, navigate]);

  const logout = async () => {
    try { await authAPI.logout(); } catch { /* session may already be expired */ }
    localStorage.removeItem('passenger_user');
    navigate('/');
  };

  const handleAccountDeleted = () => {
    localStorage.removeItem('passenger_user');
    navigate('/login');
  };

  if (!isLoggedIn) return null;

  return (
    <div dir="rtl" className="min-h-screen bg-[#F5F9FF] pb-20 md:pb-0">
      <AppNavbar />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <h1 className="text-xl font-black text-[#1E3A8A] mb-6">حسابي</h1>

        {/* ── User card ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
          <div className="flex items-center gap-4">
            <span className="w-14 h-14 rounded-2xl bg-[#1E40AF] text-white flex items-center justify-center font-black text-xl flex-shrink-0">
              {user.name?.charAt(0) || <User size={24} strokeWidth={2} />}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-black text-gray-900 text-lg truncate">{user.name || 'مسافر'}</p>
              {user.loyaltyPoints > 0 && (
                <p className="text-sm text-amber-600 font-bold flex items-center gap-1 mt-0.5">
                  <Star size={14} strokeWidth={2} className="fill-amber-500 text-amber-500" /> {user.loyaltyPoints} نقطة
                </p>
              )}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
            {user.phone && (
              <p className="text-sm text-gray-600 flex items-center gap-2">
                <Phone size={15} strokeWidth={2} className="text-gray-400 flex-shrink-0" /> <span dir="ltr">{user.phone}</span>
              </p>
            )}
            {user.email && (
              <p className="text-sm text-gray-600 flex items-center gap-2">
                <Mail size={15} strokeWidth={2} className="text-gray-400 flex-shrink-0" /> <span dir="ltr">{user.email}</span>
              </p>
            )}
          </div>
          <button
            onClick={logout}
            className="w-full mt-5 flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 font-bold py-2.5 rounded-xl transition-colors text-sm"
          >
            <LogOut size={15} strokeWidth={2} /> تسجيل الخروج
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="w-full mt-2 flex items-center justify-center gap-2 text-red-400 hover:text-red-600 font-semibold py-2.5 rounded-xl transition-colors text-xs"
          >
            <Trash2 size={13} strokeWidth={2} /> حذف الحساب
          </button>
        </div>
      </div>

      {showDeleteModal && (
        <DeleteAccountModal onClose={() => setShowDeleteModal(false)} onDeleted={handleAccountDeleted} />
      )}

      <div className="hidden md:block mt-12"><SiteFooter navigate={navigate} /></div>
      <MobileBottomNav />
    </div>
  );
};

export default PassengerProfilePage;
